<template>
  <section class="kobo-page" :dir="pageDirection">
    <div class="section-head">
      <h2>{{ tr("title") }}</h2>
      <p class="desc">{{ tr("description") }}</p>
    </div>
    <div class="grid two">
      <div class="card">
        <h3>
          <v-icon size="18">mdi-database-sync</v-icon>
          {{ tr("connection.title") }}
        </h3>
        <div class="form-grid">
          <div class="field">
            <label>{{ tr("connection.server") }}</label>
            <input v-model="server" type="text" dir="ltr" />
          </div>
          <div class="field">
            <label>{{ tr("connection.assetUid") }}</label>
            <input v-model="assetUid" type="text" dir="ltr" />
          </div>
          <div class="field">
            <label>{{ tr("connection.apiToken") }}</label>
            <div class="token-field">
              <input
                v-model="token"
                :type="showToken ? 'text' : 'password'"
                dir="ltr"
                :placeholder="tr('connection.apiTokenPlaceholder')"
              />
              <button
                type="button"
                class="token-toggle"
                :aria-label="
                  showToken ? tr('actions.hideToken') : tr('actions.showToken')
                "
                @click="showToken = !showToken"
              >
                <v-icon size="19">
                  {{ showToken ? "mdi-eye-off-outline" : "mdi-eye-outline" }}
                </v-icon>
              </button>
            </div>
          </div>
          <p class="hint">{{ tr("connection.hint") }}</p>
          <div class="field">
            <label>{{ tr("connection.heramsIntegration") }}</label>
            <div class="token-field">
              <input
                v-model="heramsToken"
                type="password"
                dir="ltr"
                :placeholder="tr('connection.heramsPlaceholder')"
                disabled
              />
              <button
                type="button"
                class="token-toggle"
                :aria-label="tr('connection.heramsIntegration')"
                disabled
              >
                <v-icon size="19"> mdi-clock-outline </v-icon>
              </button>
            </div>
          </div>
          <p class="hint">
            {{ tr("connection.heramsIntegrationHint") }}
          </p>
          <p class="hint">
            {{ tr("connection.hintHerams") }}
          </p>
          <div class="actions">
            <button class="btn" :disabled="busy" @click="testConnection">
              <v-icon size="18">mdi-lan-connect</v-icon>
              {{ tr("actions.testConnection") }}
            </button>
            <button class="btn gold" :disabled="busy" @click="pullData">
              <v-icon size="18">mdi-cloud-download-outline</v-icon>
              {{ tr("actions.pullRecords") }}
            </button>
            <a
              class="btn ghost"
              :href="koboFormUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              <v-icon size="18">mdi-open-in-new</v-icon>
              {{ tr("actions.openKobo") }}
            </a>
          </div>
        </div>
      </div>
      <div class="card">
        <h3>
          <v-icon size="18">mdi-sync-circle</v-icon>
          {{ tr("sync.title") }}
        </h3>
        <div class="status-box" :class="statusClass">
          <v-icon size="19">{{ statusIcon }}</v-icon>
          <span>{{ statusMessage }}</span>
        </div>
        <div class="grid two mt">
          <div class="kpi-card">
            <div class="kpi-label">{{ tr("sync.recordsCount") }}</div>
            <div class="kpi-value">{{ records.length }}</div>
            <div class="kpi-note">{{ tr("sync.recordsNote") }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">{{ tr("sync.lastSync") }}</div>
            <div class="kpi-value small">{{ lastSync }}</div>
            <div class="kpi-note">{{ tr("sync.deviceTime") }}</div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="records.length" class="card records-card">
      <div class="records-head">
        <h3>
          <v-icon size="18">mdi-table</v-icon>
          {{ tr("preview.title") }}
        </h3>
        <span class="records-count">
          {{ records.length }} {{ tr("preview.recordUnit") }}
        </span>
      </div>
      <div class="table-wrap desktop-records">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>{{ tr("table.governorate") }}</th>
              <th>{{ tr("table.district") }}</th>
              <th>{{ tr("table.facility") }}</th>
              <th>{{ tr("table.type") }}</th>
              <th>{{ tr("table.coordinates") }}</th>
              <th>{{ tr("table.submitted") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in records" :key="item.id">
              <td class="num">{{ item.id }}</td>
              <td>{{ item.governorate }}</td>
              <td>{{ item.district }}</td>
              <td class="facility-name">{{ item.facility }}</td>
              <td>{{ item.type }}</td>
              <td class="num">{{ item.coordinates }}</td>
              <td class="num">{{ item.submitted }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="mobile-records">
        <article
          v-for="item in records"
          :key="`mobile-${item.id}`"
          class="record-card"
        >
          <div class="record-card-head">
            <span class="record-number">#{{ item.id }}</span>
            <h4>{{ item.facility }}</h4>
          </div>
          <div class="record-card-body">
            <div class="record-row">
              <span>{{ tr("table.governorate") }}</span>
              <strong>{{ item.governorate }}</strong>
            </div>
            <div class="record-row">
              <span>{{ tr("table.district") }}</span>
              <strong>{{ item.district }}</strong>
            </div>
            <div class="record-row">
              <span>{{ tr("table.type") }}</span>
              <strong>{{ item.type }}</strong>
            </div>
            <div class="record-row">
              <span>{{ tr("table.coordinates") }}</span>
              <strong class="ltr-value">{{ item.coordinates }}</strong>
            </div>
            <div class="record-row">
              <span>{{ tr("table.submitted") }}</span>
              <strong class="ltr-value">{{ item.submitted }}</strong>
            </div>
          </div>
        </article>
      </div>
      <div class="actions mt download-actions">
        <button class="btn" @click="downloadRawJson">
          <v-icon size="18">mdi-code-json</v-icon>
          {{ tr("actions.downloadRawJson") }}
        </button>
        <button class="btn ghost" @click="downloadCsv">
          <v-icon size="18">mdi-file-delimited-outline</v-icon>
          {{ tr("actions.downloadCsv") }}
        </button>
      </div>
    </div>
  </section>
</template>
<script setup>
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import api from "@/plugins/api";
const { t, locale } = useI18n();
const tr = (key, params = {}) => t(`koboPage.${key}`, params);
const pageDirection = computed(() => (locale.value === "ar" ? "rtl" : "ltr"));
const isArabic = computed(() => locale.value === "ar");
const server = ref("https://kf.kobotoolbox.org");
const assetUid = ref("a2znBGiWqiVBDv2MKoYuSy");
const token = ref("");
const heramsToken = ref("");
const showToken = ref(false);
const busy = ref(false);
const statusKey = ref("notConnected");
const statusType = ref("info");
const lastSync = ref("-");
const records = ref([]);
const rawData = ref([]);
const statusMessage = computed(() => {
  if (statusKey.value === "recordsFetched") {
    return tr("status.recordsFetched", {
      count: records.value.length,
    });
  }
  return tr(`status.${statusKey.value}`);
});
const statusIcon = computed(() => {
  if (statusType.value === "success") return "mdi-check-circle-outline";
  if (statusType.value === "error") return "mdi-alert-circle-outline";
  if (statusType.value === "warning") return "mdi-alert-outline";
  return "mdi-information-outline";
});
const normalizedServer = computed(() =>
  server.value.trim().replace(/\/+$/, "")
);
const koboDataUrl = computed(
  () => `${normalizedServer.value}/api/v2/assets/${assetUid.value.trim()}/data/`
);
const koboFormUrl = computed(
  () => `${normalizedServer.value}/#/forms/${assetUid.value.trim()}`
);
const statusClass = computed(() => ({
  success: statusType.value === "success",
  error: statusType.value === "error",
  warning: statusType.value === "warning",
  info: statusType.value === "info",
}));
const normalizeServer = () => {
  server.value = server.value.trim().replace(/\/+$/, "");
  assetUid.value = assetUid.value.trim();
};
const validateInputs = () => {
  if (!server.value || !assetUid.value) {
    statusKey.value = "missingFields";
    statusType.value = "warning";
    return false;
  }
  return true;
};
const hasValue = (value) =>
  value !== undefined && value !== null && value !== "";
const getRawField = (item, keys) => {
  for (const key of keys) {
    if (hasValue(item[key])) {
      return item[key];
    }
  }
  // KoBo group fields are returned as group_name/field_name.
  for (const [fieldKey, value] of Object.entries(item)) {
    const leafName = fieldKey.split("/").pop();
    if (keys.includes(leafName) && hasValue(value)) {
      return value;
    }
  }
  return null;
};
const getField = (item, keys) => getRawField(item, keys) ?? tr("notAvailable");
const getCoordinates = (item) => {
  const gps = getRawField(item, [
    "_geolocation",
    "geolocation",
    "gps",
    "GPS",
    "location",
  ]);
  if (Array.isArray(gps)) {
    return gps.filter((value) => value !== null && value !== "").join(", ");
  }
  if (typeof gps === "string" && gps.trim()) {
    return gps;
  }
  return tr("notAvailable");
};
const mapRecords = (items) =>
  items.map((item, index) => ({
    id: index + 1,
    governorate: getField(item, [
      isArabic.value ? "governorate_name_ar" : "governorate_name_en",
      "governorate",
      "province",
      "province_code",
      "المحافظة",
    ]),
    district: getField(item, [
      isArabic.value ? "district_name_ar" : "district_name_en",
      "district",
      "district_code",
      "المديرية",
    ]),
    facility: getField(item, [
      isArabic.value ? "facility_name_ar" : "facility_name_en",
      "facility_name",
      "health_facility",
      "اسم_المرفق",
      "hf_name",
    ]),
    type: getField(item, [
      isArabic.value ? "facility_type_ar" : "facility_type_en",
      "facility_type",
      "facility_type_code",
      "type",
      "نوع_المرفق",
    ]),
    coordinates: getCoordinates(item),
    submitted: getField(item, [
      "_submission_time",
      "submission_time",
      "submitted_at",
    ]),
  }));
const testConnection = async () => {
  normalizeServer();
  if (!validateInputs()) return;
  busy.value = true;
  statusKey.value = "testingConnection";
  statusType.value = "info";
  try {
    await api.post("/kobo/test-connection", {
      kobo_url: koboDataUrl.value,
      api_token: token.value.trim() || null,
    });
    statusKey.value = "connectionSuccess";
    statusType.value = "success";
  } catch (error) {
    const hasTokenError = Boolean(error?.response?.data?.errors?.api_token);
    statusKey.value = hasTokenError ? "invalidToken" : "connectionFailed";
    statusType.value = "error";
  } finally {
    busy.value = false;
  }
};
const pullData = async () => {
  normalizeServer();
  if (!validateInputs()) return;
  busy.value = true;
  statusKey.value = "pullingData";
  statusType.value = "info";
  try {
    const response = await api.post("/kobo/import", {
      kobo_url: koboDataUrl.value,
      api_token: token.value.trim() || null,
      duplicate_action: "skip",
      include_data: true,
    });
    const results = Array.isArray(response.data?.data)
      ? response.data.data
      : [];
    rawData.value = results;
    records.value = mapRecords(results);
    lastSync.value = new Intl.DateTimeFormat(
      isArabic.value ? "en-US" : "en-US",
      {
        dateStyle: "medium",
        timeStyle: "short",
      }
    ).format(new Date());
    statusKey.value = "recordsFetched";
    statusType.value = "success";
  } catch (error) {
    const hasTokenError = Boolean(error?.response?.data?.errors?.api_token);
    statusKey.value = hasTokenError ? "pullInvalidToken" : "pullFailed";
    statusType.value = "error";
    console.error("KoBo import failed:", error?.response?.data || error);
  } finally {
    busy.value = false;
  }
};
const downloadFile = (content, filename, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
const downloadRawJson = () => {
  downloadFile(
    JSON.stringify(rawData.value, null, 2),
    isArabic.value ? "بيانات-kobo-الخام.json" : "kobo-raw-data.json",
    "application/json;charset=utf-8;"
  );
};
const escapeCSVValue = (value) =>
  `"${String(value ?? "").replace(/"/g, '""')}"`;
const toCSV = (rows) => {
  if (!Array.isArray(rows) || !rows.length) return "";
  const keys = Object.keys(rows[0]);
  const csvRows = [
    keys.map(escapeCSVValue).join(","),
    ...rows.map((row) => keys.map((key) => escapeCSVValue(row[key])).join(",")),
  ];
  return "\uFEFF" + csvRows.join("\n");
};
const downloadCsv = () => {
  downloadFile(
    toCSV(records.value),
    isArabic.value ? "معاينة-kobo.csv" : "kobo-preview.csv",
    "text/csv;charset=utf-8;"
  );
};
</script>
<style scoped>
.kobo-page {
  --navy: #15355c;
  --navy-deep: #0d2240;
  --gold: #b8923e;
  --bg: #f4f6f9;
  --surface: #ffffff;
  --surface-2: #f8fafc;
  --border: #dde4ec;
  --border-strong: #c3cedb;
  --text: #1a2433;
  --text-2: #4c5b6e;
  --text-3: #7a8799;
  --rag-green: #1e7a46;
  --rag-green-bg: #e7f4ec;
  --rag-amber: #b45309;
  --rag-amber-bg: #fdf3e3;
  --rag-red: #b91c1c;
  --rag-red-bg: #fbeaea;
  --shadow-sm: 0 1px 2px rgba(13, 34, 64, 0.06);
  --radius: 12px;
  --radius-sm: 8px;
  --font-num: "JetBrains Mono", monospace;
  min-height: 75vh;
  padding: 22px 26px 60px;
  overflow-x: hidden;
  background: var(--bg);
  color: var(--text);
  font-family: "Inter", "Cairo", sans-serif;
}
.kobo-page[dir="rtl"] {
  font-family: "Cairo", "Inter", sans-serif;
}
.section-head {
  margin-bottom: 18px;
}
.kobo-page[dir="rtl"] .section-head {
  text-align: right;
}
.kobo-page[dir="ltr"] .section-head {
  text-align: left;
}
.section-head h2 {
  margin: 0;
  color: var(--text);
  font-size: 23px;
  font-weight: 800;
  line-height: 1.5;
}
.section-head .desc {
  max-width: 860px;
  margin: 5px 0 0;
  color: var(--text-2);
  font-size: 13px;
  line-height: 1.8;
}
.grid {
  display: grid;
  gap: 14px;
}
.grid.two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.card {
  min-width: 0;
  padding: 18px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
}
.card + .card,
.grid + .card,
.card + .grid {
  margin-top: 16px;
}
.card h3 {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;
  color: var(--text);
  font-size: 15.5px;
  font-weight: 800;
  line-height: 1.5;
}
.form-grid {
  display: grid;
  gap: 11px;
}
.field label {
  display: block;
  margin-bottom: 5px;
  color: var(--text);
  font-size: 12px;
  font-weight: 700;
}
.field input {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 10px 12px;
  color: var(--text);
  font-family: inherit;
  font-size: 13px;
  background: var(--surface);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  outline: none;
}
.field input:focus {
  border-color: var(--navy);
  box-shadow: 0 0 0 3px rgba(21, 53, 92, 0.12);
}
.token-field {
  position: relative;
}
.token-field input {
  padding-inline-end: 48px;
}
.token-toggle {
  position: absolute;
  top: 50%;
  inset-inline-end: 5px;
  display: grid;
  width: 38px;
  height: 38px;
  padding: 0;
  place-items: center;
  color: var(--navy);
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: 7px;
  transform: translateY(-50%);
}
.token-toggle:hover {
  background: var(--surface-2);
}
.hint {
  margin: 0;
  color: var(--text-3);
  font-size: 11px;
  line-height: 1.7;
}
.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.btn {
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 8px 16px;
  color: #fff;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 700;
  text-decoration: none;
  cursor: pointer;
  background: var(--navy);
  border: 0;
  border-radius: var(--radius-sm);
  transition: opacity 0.15s, transform 0.15s;
}
.btn:hover:not(:disabled) {
  opacity: 0.92;
  transform: translateY(-1px);
}
.btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
.btn.ghost {
  color: var(--navy);
  background: var(--surface);
  border: 1px solid var(--border-strong);
}
.btn.gold {
  color: #2a1f08;
  background: var(--gold);
}
.status-box {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 12px 16px;
  color: var(--text-2);
  font-size: 13px;
  line-height: 1.7;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: var(--radius);
}
.status-box :deep(.v-icon) {
  flex-shrink: 0;
  margin-top: 1px;
}
.status-box.success {
  color: var(--rag-green);
  background: var(--rag-green-bg);
  border-color: var(--rag-green);
}
.status-box.error {
  color: var(--rag-red);
  background: var(--rag-red-bg);
  border-color: var(--rag-red);
}
.status-box.warning {
  color: var(--rag-amber);
  background: var(--rag-amber-bg);
  border-color: var(--rag-amber);
}
.kpi-card {
  padding: 14px 16px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-inline-start: 4px solid var(--border-strong);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
}
.kpi-card .kpi-label {
  color: var(--text-2);
  font-size: 12px;
  font-weight: 600;
}
.kpi-card .kpi-value {
  margin: 3px 0 2px;
  color: var(--text);
  font-family: var(--font-num);
  font-size: 26px;
  font-weight: 600;
  direction: ltr;
  text-align: start;
}
.kpi-card .kpi-value.small {
  font-family: inherit;
  font-size: 13px;
  direction: ltr;
}
.kpi-card .kpi-note {
  color: var(--text-3);
  font-size: 11.5px;
}
.records-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.records-head h3 {
  margin-bottom: 0;
}
.records-count {
  padding: 5px 10px;
  color: var(--navy);
  font-size: 11px;
  font-weight: 800;
  white-space: nowrap;
  background: #eef5fb;
  border-radius: 999px;
}
.table-wrap {
  overflow-x: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  -webkit-overflow-scrolling: touch;
}
table {
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
  background: var(--surface);
  font-size: 13px;
}
thead th {
  padding: 10px 12px;
  color: #fff;
  font-size: 12.5px;
  font-weight: 700;
  text-align: start;
  white-space: nowrap;
  background: var(--navy);
}
tbody td {
  padding: 9px 12px;
  vertical-align: middle;
  border-top: 1px solid var(--border);
}
tbody tr:hover {
  background: var(--surface-2);
}
.facility-name {
  min-width: 190px;
  font-weight: 700;
}
.num,
.ltr-value {
  font-family: var(--font-num);
  direction: ltr;
  text-align: start;
}
.mt {
  margin-top: 14px;
}
.mobile-records {
  display: none;
}
@media (max-width: 1100px) {
  .grid.two {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 768px) {
  .kobo-page {
    min-height: 100vh;
    padding: 16px 10px 42px;
  }
  .section-head h2 {
    font-size: 21px;
  }
  .card {
    padding: 14px;
    border-radius: 14px;
  }
  .actions {
    flex-direction: column;
  }
  .btn {
    width: 100%;
    min-height: 46px;
    box-sizing: border-box;
  }
  .grid.two.mt {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .records-head {
    align-items: flex-start;
  }
  .desktop-records {
    display: none;
  }
  .mobile-records {
    display: grid;
    gap: 12px;
  }
  .record-card {
    overflow: hidden;
    background: var(--surface);
    border: 1px solid var(--border);
    border-inline-start: 5px solid var(--navy);
    border-radius: 14px;
  }
  .record-card-head {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    padding: 12px;
    background: var(--surface-2);
    border-bottom: 1px solid var(--border);
  }
  .record-card-head h4 {
    min-width: 0;
    margin: 0;
    color: var(--text);
    font-size: 14px;
    font-weight: 800;
    line-height: 1.7;
    overflow-wrap: break-word;
  }
  .record-number {
    flex-shrink: 0;
    padding: 4px 7px;
    color: var(--navy);
    font-size: 10px;
    font-weight: 800;
    background: #e8f0f8;
    border-radius: 7px;
  }
  .record-card-body {
    padding: 0 12px;
  }
  .record-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px dashed var(--border);
  }
  .record-row:last-child {
    border-bottom: 0;
  }
  .record-row span {
    color: var(--text-3);
    font-size: 11px;
    font-weight: 700;
  }
  .record-row strong {
    max-width: 62%;
    color: var(--text);
    font-size: 12px;
    font-weight: 800;
    text-align: end;
    overflow-wrap: anywhere;
  }
  .download-actions {
    margin-top: 14px;
  }
}
@media (max-width: 430px) {
  .grid.two.mt {
    grid-template-columns: 1fr;
  }
  .records-head {
    flex-direction: column;
  }
  .records-count {
    align-self: flex-start;
  }
}
</style>
